import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, Home, Building2, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { servicesData } from '../mock';

const ServiceDetailPage = () => {
  const { slug } = useParams();
  const service = servicesData.find((s) => s.slug === slug);

  if (!service) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Service Not Found</h1>
          <Link to="/services">
            <Button>Back to Services</Button>
          </Link>
        </div>
      </div>
    );
  }

  const iconMap = {
    Home: Home,
    Building2: Building2,
    MapPin: MapPin,
  };

  const Icon = iconMap[service.icon];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-sky-600 to-green-600 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/services" className="inline-flex items-center text-sky-100 hover:text-white mb-6 transition-colors">
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to All Services
          </Link>
          <div className="flex items-start gap-6">
            <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center flex-shrink-0">
              <Icon className="w-10 h-10 text-white" />
            </div>
            <div>
              <h1 className="text-4xl md:text-5xl font-bold mb-4">
                {service.title}
              </h1>
              <p className="text-xl text-sky-50">
                {service.description}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-metallic-gold">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2">
              <div className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Overview</h2>
                <p className="text-lg text-gray-600 leading-relaxed">
                  {service.details.overview}
                </p>
              </div>

              <div className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">What's Included</h2>
                <div className="space-y-4">
                  {service.details.includes.map((item) => (
                    <div key={item} className="flex items-start gap-4 p-4 bg-gradient-to-br from-sky-50 to-green-50 rounded-lg border border-sky-100">
                      <CheckCircle2 className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700 font-medium">{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-gradient-to-br from-sky-50 to-green-50 border-l-4 border-sky-600 p-8 rounded-r-lg">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Ideal For</h3>
                <p className="text-gray-700 leading-relaxed">{service.details.ideal}</p>
              </div>
            </div>

            {/* Sidebar */}
            <div>
              <Card className="sticky top-24 border-2 border-sky-100 shadow-lg">
                <CardHeader>
                  <CardTitle className="text-2xl">Get Your Appraisal</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-gray-600">
                    Ready to schedule this appraisal service? Contact us today for a professional valuation.
                  </p>
                  <Link to="/contact" className="block">
                    <Button className="w-full bg-gradient-to-r from-sky-600 to-green-600 hover:from-sky-700 hover:to-green-700 text-white py-6 text-lg">
                      Schedule Now
                    </Button>
                  </Link>
                  <div className="pt-4 border-t border-gray-200">
                    <div className="space-y-3 text-sm text-gray-600">
                      <div>
                        <span className="font-semibold text-gray-900">Turnaround Time:</span>
                        <br />48-72 hours
                      </div>
                      <div>
                        <span className="font-semibold text-gray-900">Compliance:</span>
                        <br />USPAP, FHA/VA Approved
                      </div>
                      <div>
                        <span className="font-semibold text-gray-900">Service Area:</span>
                        <br />Twin Cities Metro & Wisconsin
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="mt-6">
                <Card className="border-2 border-green-100">
                  <CardHeader>
                    <CardTitle className="text-lg">Questions?</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600 text-sm mb-4">
                      Have questions about this service? We're here to help.
                    </p>
                    <Link to="/contact">
                      <Button variant="outline" className="w-full border-2 border-sky-600 text-sky-600 hover:bg-sky-50">
                        Contact Us
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetailPage;